import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { showToast } from "@/app/_module/utils/show-toast";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

import type {
  AgencyRouteSettings,
  AgencyRouteStats,
  HistoryRoutesResult,
  LiveAgent,
  RoutePlanStatus,
} from "../types/routes";

export interface RouteHistoryFilters {
  page?: number;
  limit?: number;
  agentId?: string;
  status?: RoutePlanStatus;
  startDate?: string;
  endDate?: string;
}

type Period = '6-months' | '12-months' | 'year-to-date';

// Get agency route feature settings
export const getAgencyRouteSettings = async (
  agencyId: string
): Promise<AgencyRouteSettings> => {
  const url = getApiUrl(`v1/agency/${agencyId}/routes/settings`);

  try {
    const result = await handleApiCall<AgencyRouteSettings>(async () => {
      const response = await api.get<AgencyRouteSettings>({
        url,
        auth: true,
      });
      return response;
    });

    if (result && typeof result === 'object' && 'data' in (result as any)) {
      return (result as any).data as AgencyRouteSettings;
    }
    return result as AgencyRouteSettings;
  } catch (error) {
    throw transformErrorResponse(error);
  }
};

// Update agency route feature settings
export const updateAgencyRouteSettings = async (
  agencyId: string,
  enabled: boolean
): Promise<AgencyRouteSettings> => {
  const url = getApiUrl(`v1/agency/${agencyId}/routes/settings`);

  try {
    const result = await handleApiCall<AgencyRouteSettings>(async () => {
      const response = await api.put<AgencyRouteSettings>({
        url,
        body: { enabled },
        auth: true,
      });
      return response;
    });

    if (result && typeof result === 'object' && 'data' in (result as any)) {
      return (result as any).data as AgencyRouteSettings;
    }
    return result as AgencyRouteSettings;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Get agency-wide route stats for a given period
export const getAgencyRouteStats = async (
  agencyId: string,
  period: Period = '6-months',
  startDate?: string,
  endDate?: string
): Promise<AgencyRouteStats> => {
  const queryParams = new URLSearchParams();
  queryParams.append('period', period);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);
  const url = getApiUrl(`v1/agency/${agencyId}/routes/stats?${queryParams.toString()}`);

  try {
    const result = await handleApiCall<AgencyRouteStats>(async () => {
      const response = await api.get<AgencyRouteStats>({
        url,
        auth: true,
      });
      return response;
    });

    if (result && typeof result === 'object' && 'data' in (result as any)) {
      return (result as any).data as AgencyRouteStats;
    }
    return result as AgencyRouteStats;
  } catch (error) {
    throw transformErrorResponse(error);
  }
};

// Get / update "include owners & admins in stats" feature flag
export interface StatsInclusionSettings { includeOwnersAdmins: boolean }

export const getStatsInclusion = async (
  agencyId: string
): Promise<StatsInclusionSettings> => {
  const url = getApiUrl(`v1/agency/${agencyId}/routes/stats-inclusion`);
  try {
    const result = await handleApiCall<StatsInclusionSettings>(async () => {
      const response = await api.get<StatsInclusionSettings>({ url, auth: true });
      return response;
    });
    if (result && typeof result === 'object' && 'data' in (result as any)) {
      return (result as any).data as StatsInclusionSettings;
    }
    return result as StatsInclusionSettings;
  } catch (error) {
    throw transformErrorResponse(error);
  }
};

export const updateStatsInclusion = async (
  agencyId: string,
  enabled: boolean
): Promise<StatsInclusionSettings> => {
  const url = getApiUrl(`v1/agency/${agencyId}/routes/stats-inclusion`);
  try {
    const result = await handleApiCall<StatsInclusionSettings>(async () => {
      const response = await api.put<StatsInclusionSettings>({
        url,
        body: { enabled },
        auth: true,
      });
      return response;
    });
    if (result && typeof result === 'object' && 'data' in (result as any)) {
      return (result as any).data as StatsInclusionSettings;
    }
    return result as StatsInclusionSettings;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Get paginated route history for an agency
export const getAgencyRouteHistory = async (
  agencyId: string,
  filters: RouteHistoryFilters = {}
): Promise<HistoryRoutesResult> => {
  const queryParams = new URLSearchParams();
  if (filters.page) queryParams.append('page', String(filters.page));
  if (filters.limit) queryParams.append('limit', String(filters.limit));
  if (filters.agentId) queryParams.append('agentId', filters.agentId);
  if (filters.status) queryParams.append('status', filters.status);
  if (filters.startDate) queryParams.append('startDate', filters.startDate);
  if (filters.endDate) queryParams.append('endDate', filters.endDate);
  const qs = queryParams.toString();
  const url = getApiUrl(
    `v1/agency/${agencyId}/routes/history${qs ? `?${qs}` : ''}`
  );

  try {
    const result = await handleApiCall<HistoryRoutesResult | { data: HistoryRoutesResult }>(
      async () => {
        const response = await api.get<HistoryRoutesResult | { data: HistoryRoutesResult }>({
          url,
          auth: true,
        });
        return response;
      }
    );
    if (result && typeof result === 'object' && 'data' in (result as any)) {
      return (result as any).data as HistoryRoutesResult;
    }
    return result as HistoryRoutesResult;
  } catch (error) {
    throw transformErrorResponse(error);
  }
};

// Get currently live (in_progress) agents for an agency
export const getAgencyLiveAgents = async (
  agencyId: string
): Promise<LiveAgent[]> => {
  const url = getApiUrl(`v1/agency/${agencyId}/routes/live`);

  try {
    const result = await handleApiCall<LiveAgent[] | { data: LiveAgent[] }>(async () => {
      const response = await api.get<LiveAgent[] | { data: LiveAgent[] }>({
        url,
        auth: true,
      });
      return response;
    });

    if (result && typeof result === 'object' && 'data' in (result as any)) {
      return ((result as any).data ?? []) as LiveAgent[];
    }
    return Array.isArray(result) ? result : [];
  } catch (error) {
    throw transformErrorResponse(error);
  }
};
